<script setup>
import Card from '@/components/shared/page/Card.vue'
import Avatar from '@/components/shared/core/Avatar.vue'
import {myTeammateApi} from '@/api/my-teammate.api.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {ref, computed, onMounted} from 'vue'

//Emits
const emit = defineEmits([
  'load',
])

//Navigation
const {goToAgent} = useNavigation()

//Organization
const {isAgentSurfaceEnabled} = useCurrentOrganization()

//Agent thread messages. The card is self-contained: it loads its own data
//and renders nothing while the thread is empty, so the Home view doesn't need
//to know anything about the agent feature. Note: this loads the full thread —
//if that turns out to be too heavy for Home, ask the backend for a lightweight
//thread summary instead.
const messages = ref([])
const loadThread = async () => {

  //The agent lives only on the org's selected delivery channel: when that
  //isn't the tab, skip loading entirely so the card never renders
  if (!isAgentSurfaceEnabled.value) {
    emit('load', {hasThread: false})
    return
  }

  //Swallow errors: the card is a passive entry point, so a failing or absent
  //my_teammate endpoint (e.g. feature not enabled for the org) must never
  //break the Home view — the card simply doesn't render
  try {
    const data = await myTeammateApi.getThread()
    messages.value = data.messages
  }
  catch {
    messages.value = []
  }

  //Report back whether the agent thread has messages so the parent can factor
  //the card's visibility into its own layout (e.g. Home's no-content state)
  emit('load', {hasThread: messages.value.length > 0})
}
onMounted(loadThread)

//The card only appears once the agent has reached out: the feature is
//invisible to the employee until the first agent message (same rule as the
//composer in the Agent view)
const lastMessage = computed(() => messages.value[messages.value.length - 1])
const showCard = computed(() => messages.value.length > 0)

//There is no read-tracking on the agent thread yet, so show the dot when the
//last word was the agent's — i.e. the thread is waiting on the employee
const showDot = computed(() => Boolean(lastMessage.value?.isOutgoing))
</script>

<template>
  <!--
    Pinned at the top of the enrollments list, rendered with the same shared
    Card as enrollment rows so it reads as part of the list even though the
    agent conversation is technically not an enrollment
  -->
  <Card
    v-if="showCard"
    is-clickable
    tabindex="0"
    header="Arist Agent"
    :preview="lastMessage?.body || ''"
    :timestamp="lastMessage?.sentAt"
    :show-dot="showDot"
    @click="goToAgent()"
    @keydown.enter="goToAgent()"
    @keydown.space="goToAgent()"
  >
    <template #image>
      <Avatar
        icon="smart_toy"
        size="large"
      />
    </template>
  </Card>
</template>
